import getRange from '@anm/helpers/array/createArray';
import uniq from 'lodash/fp/uniq';

import {
  Cutter,
  Direction,
  GetCurrentPageIndexProps,
  GetDirectionProps,
  GetInnerPagesProps,
  GetPagesListProps
} from '../../types';

const getOffset = (maxPagesToShow: number) => {
  const oddPagesToShow =
    maxPagesToShow % 2 === 0 ? maxPagesToShow + 1 : maxPagesToShow;

  const offset = (oddPagesToShow - 1) / 2;

  return offset;
};

const getCurrentPageIndex = ({
  arrayOfPages,
  currentPage
}: GetCurrentPageIndexProps) =>
  arrayOfPages.findIndex(page => page === currentPage);

const cutToRight = ({ arrayOfPages, currentPage, maxPagesToShow }: Cutter) => {
  const currentPageIndex = getCurrentPageIndex({ arrayOfPages, currentPage });
  const startIndex = currentPageIndex === 0 ? 1 : currentPageIndex;

  const cuttedToRight = arrayOfPages.slice(
    startIndex,
    startIndex + maxPagesToShow
  );

  return cuttedToRight;
};

const cutToLeft = ({ arrayOfPages, maxPagesToShow }: Cutter) => {
  const pagesCount = arrayOfPages.length;
  const startIndex = pagesCount - maxPagesToShow - 1;
  const endIndex = -1;

  const cuttedToLeft = arrayOfPages.slice(startIndex, endIndex);

  return cuttedToLeft;
};

const cutFromSides = ({
  currentPage,
  arrayOfPages,
  maxPagesToShow
}: Cutter) => {
  const currentPageIndex = getCurrentPageIndex({ arrayOfPages, currentPage });
  const offset = getOffset(maxPagesToShow);

  const startIndex = arrayOfPages.findIndex(
    (_, i) => currentPageIndex - offset === i
  );
  const endIndex = arrayOfPages.findIndex(
    (_, i) => currentPageIndex + offset + 1 === i
  );

  const cuttedFromSides = arrayOfPages.slice(startIndex, endIndex);

  return cuttedFromSides;
};

const getDirection = ({
  lastPage,
  currentPage
}: GetDirectionProps): Direction => {
  const isLefttDirection = lastPage - currentPage <= 1;
  const isMiddleDirection = lastPage - currentPage === 2;

  return isLefttDirection ? 'toLeft' : isMiddleDirection ? 'middle' : 'toRight';
};

const getInnerPages = ({ direction, ...cutterProps }: GetInnerPagesProps) =>
  direction === 'toRight'
    ? cutToRight(cutterProps)
    : direction === 'middle'
    ? cutFromSides(cutterProps)
    : cutToLeft(cutterProps);

const getPagesList = ({
  pagesCount,
  currentPage,
  maxPagesToShow
}: GetPagesListProps) => {
  const arrayOfPages = getRange(pagesCount, 1);

  if (arrayOfPages.length <= maxPagesToShow) return arrayOfPages;

  const firstPage = arrayOfPages[0];
  const lastPage = arrayOfPages[arrayOfPages.length - 1];

  const direction = getDirection({ lastPage, currentPage });

  const innerPages = getInnerPages({
    direction,
    currentPage,
    arrayOfPages,
    maxPagesToShow
  });

  const pages = [firstPage, ...innerPages, lastPage];

  return uniq(pages);
};

export default getPagesList;
